import { Button, Form, Input, Radio, Select } from 'antd';
import { CreateShipmentModalProps } from './component';
import {
  Project42FormSectionHeader,
  Project42ModalHeader,
  StyledAlert,
  StyledAnchor,
  StyledFilingInfoBox,
  StyledFilingTypesCard,
  StyledProject42Card,
  StyledProject42IorCard,
  StyledProject42IorWarningCard,
  StyledProject42Modal,
  StyledProject42UploadDragContainer,
  StyledProject42UploadIconButton,
} from './styles';
import { get } from 'lodash';
import { useSelector } from 'react-redux';
import { ModalKey } from 'types';
import { ANNumber, HiddenField, PickDate } from '@xbcb/form-item-components';
import {
  CssSize,
  createDataCyValue,
  DataCyPrefix,
  DataCySuffix,
} from '@xbcb/ui-types';
import { getEnv, maxLength, shouldUpdate } from '@xbcb/ui-utils';
import React, { useEffect, useState } from 'react';
import MotSelect from 'components/MotSelect';
import { SearchOutlined } from 'icons';
import { useCurrentUser, useCustomsBrokerId } from 'libs/hooks';
import Dragger from 'antd/lib/upload/Dragger';
import { InboxOutlined } from '@ant-design/icons';
import { RcFile } from 'antd/lib/upload';
import { getDocumentExtractService } from 'libs/p42';
import moment from 'moment';
import { useHistory } from 'react-router-dom';
import { createOneMutation, SearchQuery } from '@xbcb/shared-queries';
import { AnyObject, RecordType } from '@xbcb/shared-types';
import { useMutation, useQuery } from '@apollo/client';
import handleRecordCreate from 'libs/handleRecordCreate';
import { ModeOfTransport, PortCodeType } from '__generated__/graphql';
import { transformShipment } from 'libs/formTransforms';
import validateCreateShipment from 'libs/validateCreateShipment';
import LoadingSpinner from 'components/LoadingSpinner';

type UploadStep = 'NONE' | 'PROCESSING';

const Project42Modal: React.FC<CreateShipmentModalProps> = ({
  closeModal,
  openModal,
}: CreateShipmentModalProps) => {
  const MOCK_INACTIVE_IOR = 'InactiveIOR';
  const MOCK_DUPLICATE_CLIENT_ID = 'DuplicateShipment';
  const customsBrokerIdPath = ['usCustomsBroker', 'id'];
  const customsBrokerId = useCustomsBrokerId();

  const history = useHistory();
  const { visible }: { visible: boolean } = useSelector((state) =>
    get(state, `ui.modals.${ModalKey.CREATE_SHIPMENT}`, {}),
  );

  const user = useCurrentUser();
  const { operator, accountType, account } = user;

  const createMutation = createOneMutation({
    recordName: RecordType.SHIPMENT,
    fields: `
    id
  `,
  });

  const [createRecord, { loading: creating }] = useMutation(createMutation);

  const [form] = Form.useForm();
  const [searchValue, setSearchValue] = useState('');
  const [isDuplicateClientId, setIsDuplicateClientId] = useState(false);
  const [isIorActivated, setIsIorActivated] = useState(true);
  const [uploadStep, setUploadStep] = useState<UploadStep>('NONE');

  const iorSearchQueryFields = `
  id
  name
  activationStatus
  iorNumber {
    type
    value
  }
`;

  const { data: importersData, loading: importersLoading } = useQuery(
    SearchQuery({
      recordName: RecordType.US_IOR,
      fields: iorSearchQueryFields,
    }),
    {
      skip: !visible,
      variables: {
        input: {
          searchCriteria: {},
        },
      },
    },
  );

  const importers = importersData?.searchUsIors?.results || [];
  const activatedImporters = importers.filter(
    (imp: any) => imp.activationStatus === 'ACTIVATED',
  );
  const hasSingleActivatedImporter = activatedImporters.length === 1;

  const { stage } = getEnv();

  useEffect(() => {
    if (hasSingleActivatedImporter && !form.getFieldValue(['mockIor'])) {
      form.setFieldsValue({ mockIor: activatedImporters[0].id });
      setIsIorActivated(true);
    }
  }, [hasSingleActivatedImporter, activatedImporters, form]);

  const documentExtractService = getDocumentExtractService(stage, account.id);

  const handleUpload = (file: RcFile, _: RcFile[]) => {
    setUploadStep('PROCESSING');

    setTimeout(() => {
      const result = documentExtractService.extractTransportationBill({
        documentName: file.name,
      });

      const importerOfRecordName = result.importerOfRecordName ?? '';
      setSearchValue(importerOfRecordName);
      setIsIorActivated(importerOfRecordName !== MOCK_INACTIVE_IOR);

      const fieldsToSet: AnyObject = {
        ...(result.importerOfRecordName && {
          mockIor: result.importerOfRecordName,
        }),
        ...(result.masterBillNumber && { masterBill: result.masterBillNumber }),
        ...(result.houseBillNumber && { houseBills: [result.houseBillNumber] }),
        ...(result.modeOfTransport && {
          modeOfTransport: result.modeOfTransport,
        }),
        ...((result.flightNumber || result.grossWeight) && {
          conveyance: {
            ...(result.flightNumber && { tripNumber: result.flightNumber }),
            ...(result.grossWeight && { grossWeight: result.grossWeight }),
          },
        }),
        ...(result.estimatedArrivalTime && {
          arrival: {
            time: moment(result.estimatedArrivalTime),
            ...(result.portOfUnladingCode && {
              portOfUnlading: {
                type: PortCodeType.CBP,
                value: String(result.portOfUnladingCode),
              },
            }),
            ...(result.firmsCode && { firmsCode: String(result.firmsCode) }),
          },
        }),
      };

      form.setFieldsValue(fieldsToSet);

      setUploadStep('NONE');
    }, 2000);
  };

  const createDuplicateShipmentAlert = (isError: boolean) => {
    return (
      <StyledAlert
        message={
          <div>
            Duplicate shipment(s)
            <ul>
              <li>
                <StyledAnchor
                  href="/shipments/DuplicateShipment"
                  target="_blank"
                >
                  DuplicateShipment
                </StyledAnchor>
              </li>
            </ul>
          </div>
        }
        type={isError ? 'error' : 'warning'}
        showIcon
        closable={!isError}
      />
    );
  };

  const isUploading = uploadStep === 'PROCESSING';

  const docUploadCard = (
    <StyledProject42Card direction="column">
      <Project42FormSectionHeader>
        Import shipment detail from Bill of lading (optional)
      </Project42FormSectionHeader>
      <Dragger
        beforeUpload={(file, files) => handleUpload(file, files)}
        disabled={isUploading}
      >
        {isUploading ? (
          <LoadingSpinner />
        ) : (
          <StyledProject42UploadDragContainer>
            <StyledProject42UploadIconButton type="text">
              <InboxOutlined />
            </StyledProject42UploadIconButton>
            <p>Click or drag file to this area to upload</p>
            <div style={{ textAlign: 'center', color: '#00000073' }}>
              <div>Format: XLS, XLSX, PDF, DOC, DOCX</div>
              <div>File size limit: 5MB</div>
            </div>
          </StyledProject42UploadDragContainer>
        )}
      </Dragger>
    </StyledProject42Card>
  );

  const IorCard = (
    <>
      <StyledProject42IorCard>
        <div>
          <Project42FormSectionHeader>
            Importer of Record (IOR)
          </Project42FormSectionHeader>
          <Form.Item name={['mockIor']} noStyle>
            <div style={{ display: 'flex', gap: 8 }}>
              {importers.length > 0 ? (
                hasSingleActivatedImporter ? (
                  <Input
                    value={activatedImporters[0]?.name}
                    readOnly
                    style={{ flex: 3 }}
                  />
                ) : (
                  <Select
                    placeholder="Select an importer"
                    style={{ flex: 3 }}
                    loading={importersLoading}
                    onChange={(value) => {
                      const selectedImporter = importers.find(
                        (imp: any) => imp.id === value,
                      );
                      setIsIorActivated(
                        selectedImporter?.activationStatus === 'ACTIVATED',
                      );
                    }}
                  >
                    {importers.map((importer: any) => (
                      <Select.Option key={importer.id} value={importer.id}>
                        {importer.name}
                      </Select.Option>
                    ))}
                  </Select>
                )
              ) : (
                <Input
                  placeholder="Search by name"
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  suffix={
                    <SearchOutlined
                      onChange={() => {
                        const value = searchValue.trim();
                        setIsIorActivated(value !== MOCK_INACTIVE_IOR);
                      }}
                      style={{ cursor: 'pointer', color: '#999' }}
                    />
                  }
                  style={{ flex: 3 }}
                  onPressEnter={() => {
                    const value = searchValue.trim();
                    setIsIorActivated(value !== MOCK_INACTIVE_IOR);
                  }}
                />
              )}
              <div style={{ flex: 1 }} />
            </div>
          </Form.Item>
        </div>
      </StyledProject42IorCard>
      {!isIorActivated && (
        <StyledProject42IorWarningCard>
          <StyledAlert
            message={
              <div>
                This IOR is not active, please select a different IOR or{' '}
                <a href="/us-iors/MOCK_IOR" target="_blank" rel="noreferrer">
                  activate the IOR
                </a>
                , which takes X–Y business days, including a reviewing process.
              </div>
            }
            type="warning"
            showIcon
            closable
          />
        </StyledProject42IorWarningCard>
      )}
      <div style={{ height: '16px' }} />{' '}
      {/* always ensure spacing after IOR section */}
    </>
  );

  const MotCard = (
    <StyledProject42Card>
      <div>
        <Project42FormSectionHeader>
          Mode of transport
        </Project42FormSectionHeader>
        <MotSelect form={form} required />
      </div>
    </StyledProject42Card>
  );

  const modeOfTransport = Form.useWatch(['modeOfTransport'], form);
  const filingTypes = Form.useWatch('filingTypes', form);

  useEffect(() => {
    if (!modeOfTransport) return;

    const current = new Set(filingTypes || []);
    current.add('entry');

    if (modeOfTransport === 'OCEAN') {
      current.add('isf');
    } else {
      current.delete('isf');
    }

    form.setFieldsValue({ filingTypes: Array.from(current) });
  }, [modeOfTransport]);

  const getBillLabels = () => {
    switch (modeOfTransport) {
      case 'AIR':
        return {
          master: 'Master Air Waybill (MAWB)',
          house: 'House Air Waybill (HAWB)',
        };
      case 'OCEAN':
        return {
          master: 'Master Bill of Lading (MBL)',
          house: 'House Bill of Lading (HBL)',
        };
      default:
        return {
          master: 'Master Bill',
          house: 'House Bill',
        };
    }
  };

  const { master, house } = getBillLabels();

  const BillCard = (
    <StyledProject42Card direction="row">
      <div>
        <Project42FormSectionHeader>{master}</Project42FormSectionHeader>
        <Form.Item name={['masterBill']} noStyle>
          <Input placeholder="example" />
        </Form.Item>
      </div>
      <div>
        <Project42FormSectionHeader>{house}</Project42FormSectionHeader>
        <Form.Item name={['houseBills']} noStyle>
          <Input placeholder="example" />
        </Form.Item>
      </div>
    </StyledProject42Card>
  );

  const EtaCard = (
    <StyledProject42Card>
      <div>
        <Project42FormSectionHeader>
          Estimated arrival time
        </Project42FormSectionHeader>
        <PickDate
          required
          fullNamePath={['arrival', 'time']}
          localNamePath={['arrival', 'time']}
          disabledDate={() => false}
          form={form}
          dataCy={createDataCyValue(
            DataCyPrefix.ARRIVAL_ETA,
            DataCySuffix.DATE_SELECT,
          )}
        />
      </div>
    </StyledProject42Card>
  );

  const ClientIdCard = (
    <StyledProject42Card>
      <div>
        <Project42FormSectionHeader>
          Reference number (optional)
        </Project42FormSectionHeader>
        <ANNumber
          placeholder="myfavshipment"
          form={form}
          fullNamePath={['clientIdentifier']}
          $itemSize={CssSize.TINY_SHORT}
          mask={maxLength}
          max={24}
          label=""
          debounce
          // Show warning if user inputs `DuplicateShipment` to simulate duplicate
          onChange={(clientIdentifier: string) => {
            setIsDuplicateClientId(
              clientIdentifier === MOCK_DUPLICATE_CLIENT_ID,
            );
          }}
        />
        <Form.Item shouldUpdate={shouldUpdate([['clientIdentifier']])} noStyle>
          {() => isDuplicateClientId && createDuplicateShipmentAlert(true)}
        </Form.Item>
      </div>
    </StyledProject42Card>
  );

  const FilingTypesCard = ({
    modeOfTransport,
  }: {
    modeOfTransport: string;
  }) => {
    if (modeOfTransport !== ModeOfTransport.OCEAN) return null;

    return (
      <StyledFilingTypesCard>
        <StyledFilingInfoBox>
          For Ocean transportation, you will need entry filing and ISF filing.
        </StyledFilingInfoBox>
        <Project42FormSectionHeader>
          Would you like to file ISF through Amazon?
        </Project42FormSectionHeader>
        <Form.Item name="isfNeeded" initialValue={true} noStyle>
          <Radio.Group>
            <Radio value={true}>Yes</Radio>
            <Radio value={false}>No</Radio>
          </Radio.Group>
        </Form.Item>
      </StyledFilingTypesCard>
    );
  };

  return (
    <StyledProject42Modal
      destroyOnClose
      onCancel={() => {
        form.resetFields();
        closeModal();
      }}
      open={visible}
      width={665}
      footer={[
        <Button
          key="submit"
          type="primary"
          loading={creating}
          onClick={async () => {
            form.setFieldsValue({
              isfNeeded: undefined,
              mockIor: undefined,
              filingTypes: undefined,
            });
            const formValues = form.getFieldsValue(true);
            delete formValues.ior;

            await handleRecordCreate({
              form,
              history,
              recordType: RecordType.SHIPMENT,
              transformCreateRecordInput: transformShipment.toCreateSchema,
              validateCreate: validateCreateShipment,
              createRecord,
              clean: false,
              closeModal,
              user,
              additionalInputValues: {
                ...formValues,
                ior: {
                  ior: {
                    id: 'usIor_513fda37-ca20-43ea-adb5-efdb5ce99d76',
                  },
                },
                forwarder: {
                  id: 'forwarder_0c39b083-7e4a-5eaf-b607-6f59d1a42089',
                },
                shipper: {
                  shipper: {
                    id: 'shipper_6696bfd4-017a-4aef-87b6-d5736152de98',
                  },
                },
                sourceCountry: 'CN',
                destinationCountry: 'US',
              },
            });
          }}
        >
          Create
        </Button>,
      ]}
    >
      <Project42ModalHeader>Create shipment</Project42ModalHeader>
      <Form form={form}>
        {docUploadCard}
        {IorCard}
        {MotCard}
        {BillCard}
        {EtaCard}
        {ClientIdCard}
        {<FilingTypesCard modeOfTransport={modeOfTransport} />}
        <HiddenField
          localNamePath={customsBrokerIdPath}
          fullNamePath={customsBrokerIdPath}
          value={customsBrokerId}
          form={form}
        />
        <HiddenField
          localNamePath={['arrival', 'portOfUnlading', 'value']}
          fullNamePath={['arrival', 'portOfUnlading', 'value']}
          value={undefined}
          form={form}
        />
        <HiddenField
          localNamePath={['arrival', 'portOfUnlading', 'type']}
          fullNamePath={['arrival', 'portOfUnlading', 'type']}
          value={undefined}
          form={form}
        />
        <HiddenField
          localNamePath={['arrival', 'firmsCode']}
          fullNamePath={['arrival', 'firmsCode']}
          value={undefined}
          form={form}
        />
      </Form>
    </StyledProject42Modal>
  );
};

export default Project42Modal;
